import { useState, useEffect, useCallback, useRef } from 'react';

interface RoadSegConfig {
  conf: number;
  imgsz: number;
  retina_masks: boolean;
  morph_size: number;
  morph_iter: number;
  fill_holes_area: number;
  dilate: number;
  downsample: number;
  edge_blend: number;
  adaptive_skip: boolean;
  change_thresh: number;
  jpeg_quality: number;
  detect_with_seg: boolean;
  det_conf: number;
  det_imgsz: number;
  det_max: number;
  mask_stale_ms: number;
  skip_sleep_ms: number;
  infer_sleep_ms: number;
}

const DEFAULTS: RoadSegConfig = {
  conf: 0.15,
  imgsz: 320,
  retina_masks: true,
  morph_size: 55,
  morph_iter: 2,
  fill_holes_area: 12000,
  dilate: 15,
  downsample: 6,
  edge_blend: 5,
  adaptive_skip: true,
  change_thresh: 0.04,
  jpeg_quality: 75,
  detect_with_seg: true,
  det_conf: 0.35,
  det_imgsz: 640,
  det_max: 30,
  mask_stale_ms: 300,
  skip_sleep_ms: 10,
  infer_sleep_ms: 5,
};

const PARAM_INFO: Record<string, string> = {
  conf: 'Confidence threshold — lower catches more road but risks false positives',
  imgsz: 'Model input resolution — lower = faster inference, higher = better quality',
  retina_masks: 'Pixel-accurate masks at full resolution — small speed cost, big quality gain',
  morph_size: 'Morphological closing kernel — fills gaps from cars/shadows (0 = off)',
  morph_iter: 'Closing iterations — more iterations fill bigger gaps but cost more',
  fill_holes_area: 'Fill holes smaller than this area in px² — removes car-shaped gaps',
  dilate: 'Expand road mask outward — fills gaps between nearby road segments (0 = off, 5–25 typical)',
  downsample: 'Blur strength — higher = more blur on non-road areas, faster',
  edge_blend: 'Edge feathering — softens the road/non-road boundary (0 = hard edge)',
  adaptive_skip: 'Skip inference when the scene barely changed — saves GPU cycles',
  change_thresh: 'Frame change threshold — higher skips more frames (saves network round-trips)',
  jpeg_quality: 'MJPEG stream quality — lower = faster encoding, less bandwidth',
  detect_with_seg: 'Run object detection alongside road segmentation (GPU combined mode)',
  det_conf: 'Detection confidence threshold — higher = fewer but more certain detections',
  det_imgsz: 'Detection input resolution — higher = better at finding small objects',
  det_max: 'Maximum number of detected objects per frame',
  mask_stale_ms: 'Drop mask if older than this — prevents misaligned overlay after fast camera pans (0 = disabled)',
  skip_sleep_ms: 'Sleep after skipping a frame — lower = tighter loop, more CPU (1ms for local GPU, 10ms for remote)',
  infer_sleep_ms: 'Sleep after inference — lower = tighter loop, more CPU (1ms for local GPU, 5ms for remote)',
};

interface GpuModel {
  loaded: boolean;
  format: string;
  imgsz: number;
}

interface GpuMetrics {
  gpu_name?: string;
  gpu_util_pct?: number;
  mem_util_pct?: number;
  mem_used_mb?: number;
  mem_total_mb?: number;
  temperature_c?: number;
  power_w?: number;
}

interface SystemMetrics {
  cpu_pct?: number;
  cpu_count?: number;
  mem_used_mb?: number;
  mem_total_mb?: number;
  mem_pct?: number;
}

interface GpuStatus {
  status: string;
  combined_inference?: boolean;
  seg_model?: GpuModel;
  det_model?: GpuModel;
  infer_count?: number;
  avg_seg_ms?: number;
  avg_det_ms?: number;
  avg_combined_ms?: number;
  model_loaded?: boolean;
  model_format?: string;
  imgsz?: number;
  avg_infer_ms?: number;
  gpu?: GpuMetrics;
  system?: SystemMetrics;
}

interface RoadSegSettingsProps {
  apiUrl: string;
  authHeaders: (json?: boolean) => HeadersInit;
  roadSegMode: 'blur' | 'overlay';
}

export default function RoadSegSettings({ apiUrl, authHeaders, roadSegMode }: RoadSegSettingsProps) {
  const [config, setConfig] = useState<RoadSegConfig>(DEFAULTS);
  const [inferenceSource, setInferenceSource] = useState<string>('local');
  const [sourceOverride, setSourceOverride] = useState<string | null>(null);
  const [availableSources, setAvailableSources] = useState<string[]>(['local']);
  const [gpuCombined, setGpuCombined] = useState(false);
  const [gpu, setGpu] = useState<GpuStatus | null>(null);
  const [isLocalInference, setIsLocalInference] = useState(false);

  useEffect(() => {
    fetch(`${apiUrl}/api/drone/road-seg`, { headers: authHeaders() })
      .then(r => r.json())
      .then(data => {
        if (data.config) setConfig({ ...DEFAULTS, ...data.config });
        if (data.inference_source) setInferenceSource(data.inference_source);
        if (data.inference_source_override !== undefined) setSourceOverride(data.inference_source_override);
        if (data.available_sources) setAvailableSources(data.available_sources);
        if (data.gpu_combined !== undefined) setGpuCombined(data.gpu_combined);
        if (data.gpu) setGpu(data.gpu);
        if (data.is_local_inference !== undefined) setIsLocalInference(data.is_local_inference);
      })
      .catch(() => {});
  }, [apiUrl]);

  const switchSource = useCallback(async (source: string) => {
    setSourceOverride(source === 'auto' ? null : source);
    try {
      const resp = await fetch(`${apiUrl}/api/drone/road-seg/source`, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({ source }),
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.inference_source) setInferenceSource(data.inference_source);
        if (data.inference_source_override !== undefined) setSourceOverride(data.inference_source_override);
      }
    } catch {
      console.error('Failed to switch inference source');
    }
  }, [apiUrl, authHeaders]);

  const applyChange = useCallback(async (key: keyof RoadSegConfig, value: number | boolean) => {
    setConfig(prev => ({ ...prev, [key]: value }));
    try {
      await fetch(`${apiUrl}/api/drone/road-seg/config`, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({ [key]: value }),
      });
    } catch {
      console.error(`Failed to update road seg config: ${key}`);
    }
  }, [apiUrl, authHeaders]);

  const resetDefaults = useCallback(async () => {
    setConfig(DEFAULTS);
    try {
      await fetch(`${apiUrl}/api/drone/road-seg/config`, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify(DEFAULTS),
      });
    } catch {
      console.error('Failed to reset road seg config');
    }
  }, [apiUrl, authHeaders]);

  const sliderRow = (label: string, key: keyof RoadSegConfig, min: number, max: number, step: number, unit?: string) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }} title={PARAM_INFO[key]}>
      <label style={{ width: 90, fontSize: 11, color: 'var(--color-text-secondary)', flexShrink: 0, cursor: 'help' }}>{label}</label>
      <input
        type="range"
        min={min} max={max} step={step}
        value={config[key] as number}
        onChange={e => applyChange(key, parseFloat(e.target.value))}
        style={{ flex: 1, height: 14, accentColor: 'var(--color-warning)' }}
      />
      <span style={{ width: 45, fontSize: 11, color: 'var(--color-text-primary)', textAlign: 'right' }}>
        {config[key] as number}{unit || ''}
      </span>
    </div>
  );

  const toggleRow = (label: string, key: keyof RoadSegConfig, desc?: string) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }} title={PARAM_INFO[key]}>
      <label style={{ width: 90, fontSize: 11, color: 'var(--color-text-secondary)', flexShrink: 0, cursor: 'help' }}>{label}</label>
      <input
        type="checkbox"
        checked={config[key] as boolean}
        onChange={e => applyChange(key, e.target.checked)}
        style={{ accentColor: 'var(--color-warning)' }}
      />
      {desc && <span style={{ fontSize: 10, color: 'var(--color-text-muted)' }}>{desc}</span>}
    </div>
  );

  const [stats, setStats] = useState<Record<string, any> | null>(null);
  const [showStats, setShowStats] = useState(false);
  const statsTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const [recording, setRecording] = useState(false);
  const recordingRef = useRef(false);
  const recorded = useRef<{ ts: string; data: Record<string, any> }[]>([]);

  const stopRecording = useCallback(() => {
    setRecording(false);
    recordingRef.current = false;
    const rows = recorded.current;
    if (rows.length === 0) return;
    const header = [
      'timestamp', 'source',
      'infer_total_avg', 'infer_total_min', 'infer_total_max',
      'infer_encode_avg', 'infer_http_avg', 'infer_server_avg',
      'infer_smooth_avg', 'network_overhead',
      'inferred_frames', 'skipped_frames', 'skip_ratio', 'mask_stale_drops',
      'mjpeg_compose_avg', 'mjpeg_encode_avg', 'mjpeg_fps',
      'webrtc_compose_avg', 'webrtc_fps',
      'cpu_pct', 'mem_pct', 'mem_used_mb', 'mem_total_mb',
      'gpu_util_pct', 'gpu_vram_used_mb', 'gpu_vram_total_mb', 'gpu_temp_c', 'gpu_power_w',
      'remote_cpu_pct', 'remote_mem_pct',
    ];
    const csvRows = [header.join(',')];
    for (const { ts, data } of rows) {
      const inf = data.inference || {};
      const mj = data.mjpeg || {};
      const wr = data.webrtc || {};
      const sys = data.system || {};
      const gpu = sys.local_gpu || sys.remote_gpu || {};
      const rsys = sys.remote_system || {};
      csvRows.push([
        ts, inf.source || '',
        inf.total?.avg_ms ?? '', inf.total?.min_ms ?? '', inf.total?.max_ms ?? '',
        inf.encode?.avg_ms ?? '', inf.http_roundtrip?.avg_ms ?? '', inf.server?.avg_ms ?? '',
        inf.smooth_mask?.avg_ms ?? '', inf.network_overhead_ms ?? '',
        inf.inferred_frames ?? '', inf.skipped_frames ?? '', inf.skip_ratio ?? '', inf.mask_stale_drops ?? '',
        mj.compose?.avg_ms ?? '', mj.encode?.avg_ms ?? '', mj.fps ?? '',
        wr.compose?.avg_ms ?? '', wr.fps ?? '',
        sys.cpu_pct ?? '', sys.mem_pct ?? '', sys.mem_used_mb ?? '', sys.mem_total_mb ?? '',
        gpu.gpu_util_pct ?? '', gpu.mem_used_mb ?? '', gpu.mem_total_mb ?? '', gpu.temperature_c ?? '', gpu.power_w ?? '',
        rsys.cpu_pct ?? '', rsys.mem_pct ?? '',
      ].join(','));
    }
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pipeline-stats-${new Date().toISOString().slice(0, 19).replace(/:/g, '')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    recorded.current = [];
  }, []);

  useEffect(() => {
    if (!showStats && recordingRef.current) stopRecording();
    const interval = showStats ? 2000 : 5000;
    const poll = () => {
      fetch(`${apiUrl}/api/drone/pipeline-stats`, { headers: authHeaders() })
        .then(r => r.ok ? r.json() : null)
        .then(d => {
          if (!d) return;
          setStats(d);
          if (recordingRef.current) {
            recorded.current.push({ ts: new Date().toISOString(), data: d });
          }
        })
        .catch(() => {});
    };
    poll();
    statsTimer.current = setInterval(poll, interval);
    return () => { if (statsTimer.current) clearInterval(statsTimer.current); };
  }, [showStats, apiUrl]);

  useEffect(() => {
    return () => {
      if (recordingRef.current) stopRecording();
    };
  }, [stopRecording]);

  const isGpu = inferenceSource === 'gpu-combined' || gpuCombined;
  const isJetson = inferenceSource === 'jetson' && !gpuCombined;

  const statLine = (label: string, data: Record<string, any> | undefined, unit = 'ms') => {
    if (!data || !data.avg_ms) return null;
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, padding: '1px 0' }}>
        <span style={{ color: 'var(--color-text-secondary)' }}>{label}</span>
        <span style={{ color: 'var(--color-text-primary)', fontFamily: 'var(--font-mono)' }}>
          {data.avg_ms}{unit} <span style={{ color: 'var(--color-text-muted)' }}>({data.min_ms}–{data.max_ms})</span>
        </span>
      </div>
    );
  };

  return (
    <div style={{
      background: 'rgba(0,0,0,0.85)', padding: '8px 10px', fontSize: 11,
      borderBottom: '1px solid var(--color-border)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontWeight: 600, color: 'var(--color-warning)', fontSize: 11 }}>
          {isGpu ? 'GPU INFERENCE' : 'ROAD SEG SETTINGS'}
        </span>
        <button
          onClick={resetDefaults}
          style={{
            background: 'transparent', border: '1px solid var(--color-border-light)', color: 'var(--color-text-secondary)',
            fontSize: 10, padding: '1px 6px', borderRadius: 3, cursor: 'pointer',
          }}
        >
          Reset
        </button>
      </div>

      {availableSources.length > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
          <label style={{ fontSize: 10, color: 'var(--color-text-secondary)', flexShrink: 0 }}>Inference</label>
          <div style={{ display: 'flex', gap: 2, flex: 1 }}>
            {['local', 'remote', 'auto'].map(src => {
              const active = (sourceOverride ?? 'auto') === src;
              return (
                <button
                  key={src}
                  onClick={() => switchSource(src)}
                  style={{
                    flex: 1, fontSize: 10, padding: '2px 0', borderRadius: 3, cursor: 'pointer',
                    background: active ? (src === 'local' ? '#1a3a1a' : src === 'remote' ? 'var(--color-bg-raised)' : '#2a2a1a') : 'transparent',
                    border: `1px solid ${active ? (src === 'local' ? '#0a4' : src === 'remote' ? '#08f' : '#aa0') : '#444'}`,
                    color: active ? (src === 'local' ? 'var(--color-accent)' : src === 'remote' ? 'var(--color-info)' : '#cc0') : 'var(--color-text-muted)',
                  }}
                >
                  {src === 'local' ? 'CPU' : src === 'remote' ? 'GPU' : 'Auto'}
                </button>
              );
            })}
          </div>
          <span style={{ fontSize: 9, color: 'var(--color-text-muted)', flexShrink: 0 }}>
            {inferenceSource === 'local' ? 'CPU' : inferenceSource === 'gpu-combined' ? 'GPU+Det' : 'Jetson'}
          </span>
        </div>
      )}

      {isGpu && gpu && (
        <div style={{
          background: gpu.status === 'ok' ? 'rgba(0,120,255,0.12)' : 'rgba(255,0,0,0.15)',
          border: `1px solid ${gpu.status === 'ok' ? '#0066cc' : '#a00'}`,
          borderRadius: 3, padding: '4px 6px', marginBottom: 6, fontSize: 10,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
            <span style={{ color: gpu.status === 'ok' ? 'var(--color-info)' : 'var(--color-danger)' }}>
              {gpu.status === 'ok' ? '●' : '○'} GPU Combined
            </span>
            {gpu.status === 'ok' && gpu.avg_combined_ms !== undefined && (
              <span style={{ color: 'var(--color-text-secondary)' }}>
                {gpu.avg_combined_ms}ms avg | {gpu.infer_count} frames
                {stats?.inference?.skip_ratio != null && <span style={{ color: 'var(--color-warning)' }}>{' | skip '}{Math.round(stats.inference.skip_ratio * 100)}%</span>}
              </span>
            )}
          </div>
          {gpu.status === 'ok' && gpu.seg_model && gpu.det_model && (
            <div style={{ color: 'var(--color-text-muted)', fontSize: 9, display: 'flex', gap: 12 }}>
              <span>SEG: {gpu.seg_model.format?.toUpperCase()} {gpu.seg_model.imgsz} ({gpu.avg_seg_ms}ms)</span>
              <span>DET: {gpu.det_model.format?.toUpperCase()} {gpu.det_model.imgsz} ({gpu.avg_det_ms}ms)</span>
            </div>
          )}
          {gpu.status === 'ok' && (() => {
            const gm = stats?.system?.remote_gpu || stats?.system?.local_gpu || gpu.gpu;
            const sm = stats?.system?.remote_system || gpu.system;
            if (!gm && !sm) return null;
            const tc = (v: number | undefined) => v == null ? null : v > 85 ? '#f44' : v > 70 ? '#e8a735' : '#ccc';
            return (
              <div style={{ fontSize: 9, marginTop: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {gm && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, color: 'var(--color-text-secondary)' }}>
                    {gm.gpu_util_pct != null && <span>GPU: <span style={{ color: gm.gpu_util_pct > 90 ? 'var(--color-danger)' : gm.gpu_util_pct > 70 ? 'var(--color-warning)' : 'var(--color-info)' }}>{gm.gpu_util_pct}%</span></span>}
                    {gm.mem_used_mb != null && gm.mem_total_mb != null && <span>VRAM: {(gm.mem_used_mb / 1024).toFixed(1)}/{(gm.mem_total_mb / 1024).toFixed(1)}G</span>}
                    {gm.temperature_c != null && <span style={{ color: tc(gm.temperature_c) ?? undefined }}>{gm.temperature_c}°C</span>}
                    {gm.power_w != null && <span>{gm.power_w}W</span>}
                  </div>
                )}
                {sm && sm.cpu_pct != null && (
                  <div style={{ display: 'flex', gap: 8, color: 'var(--color-text-secondary)' }}>
                    <span>CPU: <span style={{ color: sm.cpu_pct > 90 ? 'var(--color-danger)' : sm.cpu_pct > 70 ? 'var(--color-warning)' : 'var(--color-text-primary)' }}>{sm.cpu_pct.toFixed?.(0) ?? sm.cpu_pct}%</span></span>
                    {sm.mem_used_mb != null && sm.mem_total_mb != null && <span>RAM: {(sm.mem_used_mb / 1024).toFixed(1)}/{(sm.mem_total_mb / 1024).toFixed(1)}G</span>}
                  </div>
                )}
              </div>
            );
          })()}
          {gpu.status === 'unreachable' && (
            <span style={{ color: 'var(--color-danger)', marginLeft: 8 }}>unreachable</span>
          )}
        </div>
      )}

      {isJetson && gpu && (
        <div style={{
          background: gpu.status === 'ok' ? 'rgba(0,180,0,0.15)' : 'rgba(255,0,0,0.15)',
          border: `1px solid ${gpu.status === 'ok' ? '#0a4' : '#a00'}`,
          borderRadius: 3, padding: '3px 6px', marginBottom: 6, fontSize: 10,
        }}>
          <span style={{ color: gpu.status === 'ok' ? 'var(--color-accent)' : 'var(--color-danger)' }}>
            {gpu.status === 'ok' ? '●' : '○'} Jetson GPU
          </span>
          {gpu.status === 'ok' && (
            <span style={{ color: 'var(--color-text-secondary)', marginLeft: 8 }}>
              {(gpu.model_format || gpu.seg_model?.format)?.toUpperCase()}
              {(gpu.imgsz || gpu.seg_model?.imgsz) ? ` ${gpu.imgsz || gpu.seg_model?.imgsz}` : ''}
              {' | '}{gpu.avg_infer_ms || gpu.avg_seg_ms}ms avg | {gpu.infer_count} frames
              {stats?.inference?.skip_ratio != null && <span style={{ color: 'var(--color-warning)' }}>{' | skip '}{Math.round(stats.inference.skip_ratio * 100)}%</span>}
            </span>
          )}
          {gpu.status === 'ok' && (() => {
            const gm = stats?.system?.remote_gpu || gpu.gpu;
            const sm = stats?.system?.remote_system || gpu.system;
            if (!gm && !sm) return null;
            const tc = (v: number | undefined) => v == null ? null : v > 85 ? '#f44' : v > 70 ? '#e8a735' : '#ccc';
            return (
              <div style={{ fontSize: 9, marginTop: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {gm && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, color: 'var(--color-text-secondary)' }}>
                    {gm.gpu_util_pct != null && <span>GPU: <span style={{ color: gm.gpu_util_pct > 90 ? 'var(--color-danger)' : gm.gpu_util_pct > 70 ? 'var(--color-warning)' : 'var(--color-accent)' }}>{gm.gpu_util_pct}%</span></span>}
                    {gm.mem_used_mb != null && gm.mem_total_mb != null && <span>VRAM: {(gm.mem_used_mb / 1024).toFixed(1)}/{(gm.mem_total_mb / 1024).toFixed(1)}G</span>}
                    {gm.temperature_c != null && <span style={{ color: tc(gm.temperature_c) ?? undefined }}>{gm.temperature_c}°C</span>}
                    {gm.power_w != null && <span>{gm.power_w}W</span>}
                  </div>
                )}
                {sm && sm.cpu_pct != null && (
                  <div style={{ display: 'flex', gap: 8, color: 'var(--color-text-secondary)' }}>
                    <span>CPU: <span style={{ color: sm.cpu_pct > 90 ? 'var(--color-danger)' : sm.cpu_pct > 70 ? 'var(--color-warning)' : 'var(--color-text-primary)' }}>{sm.cpu_pct.toFixed?.(0) ?? sm.cpu_pct}%</span></span>
                    {sm.mem_used_mb != null && sm.mem_total_mb != null && <span>RAM: {(sm.mem_used_mb / 1024).toFixed(1)}/{(sm.mem_total_mb / 1024).toFixed(1)}G</span>}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      <div style={{ color: 'var(--color-text-muted)', fontSize: 10, marginBottom: 4 }}>Segmentation</div>
      {sliderRow('Confidence', 'conf', 0.01, 0.5, 0.01)}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }} title={PARAM_INFO.imgsz}>
        <label style={{ width: 90, fontSize: 11, color: 'var(--color-text-secondary)', flexShrink: 0, cursor: 'help' }}>Input size</label>
        <select
          value={config.imgsz}
          onChange={e => applyChange('imgsz', parseInt(e.target.value))}
          style={{
            flex: 1, background: 'var(--color-bg-raised)', color: 'var(--color-text-primary)',
            border: '1px solid var(--color-border-light)', fontSize: 11, padding: '1px 4px', borderRadius: 3,
          }}
        >
          <option value={320}>320 (fast)</option>
          <option value={480}>480 (balanced)</option>
          <option value={640}>640 (quality)</option>
          {isGpu && <option value={1280}>1280 (max)</option>}
        </select>
      </div>
      {toggleRow('Retina masks', 'retina_masks', 'pixel-accurate (slower)')}

      {isGpu && (
        <>
          <div style={{ color: 'var(--color-text-muted)', fontSize: 10, marginBottom: 4, marginTop: 6 }}>Object Detection</div>
          {toggleRow('Detection', 'detect_with_seg', 'detect objects in same pass')}
          {config.detect_with_seg && (
            <>
              {sliderRow('Det conf', 'det_conf', 0.05, 0.9, 0.05)}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }} title={PARAM_INFO.det_imgsz}>
                <label style={{ width: 90, fontSize: 11, color: 'var(--color-text-secondary)', flexShrink: 0, cursor: 'help' }}>Det size</label>
                <select
                  value={config.det_imgsz}
                  onChange={e => applyChange('det_imgsz', parseInt(e.target.value))}
                  style={{
                    flex: 1, background: 'var(--color-bg-raised)', color: 'var(--color-text-primary)',
                    border: '1px solid var(--color-border-light)', fontSize: 11, padding: '1px 4px', borderRadius: 3,
                  }}
                >
                  <option value={320}>320 (fast)</option>
                  <option value={480}>480 (balanced)</option>
                  <option value={640}>640 (quality)</option>
                  <option value={1280}>1280 (max)</option>
                </select>
              </div>
              {sliderRow('Max objects', 'det_max', 1, 100, 1)}
            </>
          )}
        </>
      )}

      <div style={{ color: 'var(--color-text-muted)', fontSize: 10, marginBottom: 4, marginTop: 6 }}>
        Performance{isLocalInference && <span style={{ color: 'var(--color-accent)', fontSize: 9, marginLeft: 4 }}>(local GPU)</span>}
      </div>
      {toggleRow('Adaptive skip', 'adaptive_skip', 'skip when scene unchanged')}
      {config.adaptive_skip && sliderRow('Change thresh', 'change_thresh', 0.005, 0.1, 0.005)}
      {sliderRow('Stale guard', 'mask_stale_ms', 0, 1000, 50, 'ms')}
      {sliderRow('Skip sleep', 'skip_sleep_ms', 0, 50, 1, 'ms')}
      {sliderRow('Infer sleep', 'infer_sleep_ms', 0, 50, 1, 'ms')}

      <div style={{ color: 'var(--color-text-muted)', fontSize: 10, marginBottom: 4, marginTop: 6 }}>Stream</div>
      {sliderRow('JPEG quality', 'jpeg_quality', 30, 95, 5)}

      <div style={{ color: 'var(--color-text-muted)', fontSize: 10, marginBottom: 4, marginTop: 6 }}>Post-processing</div>
      {sliderRow('Morph size', 'morph_size', 0, 65, 1, 'px')}
      {sliderRow('Morph iter', 'morph_iter', 0, 5, 1)}
      {sliderRow('Fill holes', 'fill_holes_area', 0, 25000, 500, 'px')}
      {sliderRow('Dilate', 'dilate', 0, 35, 1, 'px')}

      {roadSegMode === 'blur' && (
        <>
          <div style={{ color: 'var(--color-text-muted)', fontSize: 10, marginBottom: 4, marginTop: 6 }}>Blur</div>
          {sliderRow('Blur strength', 'downsample', 2, 12, 1, 'x')}
          {sliderRow('Edge blend', 'edge_blend', 0, 15, 1)}
        </>
      )}

      <div style={{ marginTop: 8, borderTop: '1px solid var(--color-border)', paddingTop: 6 }}>
        <div
          onClick={() => setShowStats(s => !s)}
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
        >
          <span style={{ color: 'var(--color-text-muted)', fontSize: 10 }}>Pipeline Stats</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {showStats && (
              <button
                onClick={e => {
                  e.stopPropagation();
                  if (recording) {
                    stopRecording();
                  } else {
                    recorded.current = [];
                    recordingRef.current = true;
                    setRecording(true);
                  }
                }}
                style={{
                  background: recording ? '#a00' : 'transparent',
                  border: `1px solid ${recording ? '#f44' : '#555'}`,
                  color: recording ? '#fff' : 'var(--color-text-secondary)',
                  fontSize: 9, padding: '1px 6px', borderRadius: 3, cursor: 'pointer',
                }}
              >
                {recording ? `Stop (${recorded.current.length})` : 'Record'}
              </button>
            )}
            <span style={{ color: 'var(--color-text-muted)', fontSize: 9 }}>{showStats ? '▼' : '▶'}</span>
          </div>
        </div>
        {showStats && stats && (
          <div style={{ marginTop: 4 }}>
            {stats.system && (
              <div style={{ marginBottom: 6 }}>
                <div style={{ color: 'var(--color-text-muted)', fontSize: 9, marginBottom: 2 }}>SYSTEM</div>
                {(() => {
                  const barStyle = (pct: number, color?: string) => ({
                    width: 60, height: 6, background: '#222', borderRadius: 3, overflow: 'hidden' as const, flexShrink: 0,
                  });
                  const fillColor = (pct: number) => pct > 90 ? '#f44' : pct > 70 ? '#e8a735' : '#0c6';
                  const fillStyle = (pct: number, color?: string) => ({
                    width: `${Math.min(pct, 100)}%`, height: '100%',
                    background: color || fillColor(pct), borderRadius: 3, transition: 'width 0.3s',
                  });
                  const rowStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 10, padding: '1px 0' } as const;
                  const monoStyle = { fontFamily: 'var(--font-mono)', fontSize: 9 } as const;
                  const sys = stats.system;
                  const gpu = sys.local_gpu || sys.remote_gpu;
                  const isRemote = !sys.local_gpu && !!sys.remote_gpu;
                  const rsys = sys.remote_system;

                  return (
                    <>
                      <div style={rowStyle}>
                        <span style={{ color: 'var(--color-text-secondary)' }}>CPU ({sys.cpu_count} cores)</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div style={barStyle(sys.cpu_pct)}>
                            <div style={fillStyle(sys.cpu_pct)} />
                          </div>
                          <span style={{ color: fillColor(sys.cpu_pct), ...monoStyle, minWidth: 32, textAlign: 'right' as const }}>
                            {sys.cpu_pct.toFixed?.(0) ?? sys.cpu_pct}%
                          </span>
                        </div>
                      </div>
                      <div style={rowStyle}>
                        <span style={{ color: 'var(--color-text-secondary)' }}>Memory</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div style={barStyle(sys.mem_pct)}>
                            <div style={fillStyle(sys.mem_pct)} />
                          </div>
                          <span style={{ color: 'var(--color-text-primary)', ...monoStyle, minWidth: 60, textAlign: 'right' as const }}>
                            {(sys.mem_used_mb / 1024).toFixed(1)}/{(sys.mem_total_mb / 1024).toFixed(1)}G
                          </span>
                        </div>
                      </div>

                      {rsys && rsys.cpu_pct != null && (
                        <>
                          <div style={{ ...rowStyle, marginTop: 3 }}>
                            <span style={{ color: 'var(--color-text-secondary)' }}>GPU Host CPU</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <div style={barStyle(rsys.cpu_pct)}>
                                <div style={fillStyle(rsys.cpu_pct)} />
                              </div>
                              <span style={{ color: fillColor(rsys.cpu_pct), ...monoStyle, minWidth: 32, textAlign: 'right' as const }}>
                                {rsys.cpu_pct.toFixed?.(0) ?? rsys.cpu_pct}%
                              </span>
                            </div>
                          </div>
                          {rsys.mem_pct != null && (
                            <div style={rowStyle}>
                              <span style={{ color: 'var(--color-text-secondary)' }}>GPU Host Mem</span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <div style={barStyle(rsys.mem_pct)}>
                                  <div style={fillStyle(rsys.mem_pct)} />
                                </div>
                                <span style={{ color: 'var(--color-text-primary)', ...monoStyle, minWidth: 60, textAlign: 'right' as const }}>
                                  {(rsys.mem_used_mb / 1024).toFixed(1)}/{(rsys.mem_total_mb / 1024).toFixed(1)}G
                                </span>
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {gpu && (
                        <>
                          <div style={{ ...rowStyle, marginTop: 3 }}>
                            <span style={{ color: 'var(--color-text-secondary)' }}>
                              GPU{isRemote ? ' (remote)' : ''}{gpu.gpu_name ? ` — ${gpu.gpu_name}` : ''}
                            </span>
                          </div>
                          {gpu.gpu_util_pct != null && (
                            <div style={{ ...rowStyle, paddingLeft: 8 }}>
                              <span style={{ color: 'var(--color-text-muted)' }}>Util</span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <div style={barStyle(gpu.gpu_util_pct)}>
                                  <div style={fillStyle(gpu.gpu_util_pct, '#4a9eff')} />
                                </div>
                                <span style={{ color: 'var(--color-text-primary)', ...monoStyle, minWidth: 32, textAlign: 'right' as const }}>
                                  {gpu.gpu_util_pct}%
                                </span>
                              </div>
                            </div>
                          )}
                          {gpu.mem_used_mb != null && gpu.mem_total_mb != null && (
                            <div style={{ ...rowStyle, paddingLeft: 8 }}>
                              <span style={{ color: 'var(--color-text-muted)' }}>VRAM</span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <div style={barStyle(gpu.mem_used_mb / gpu.mem_total_mb * 100)}>
                                  <div style={fillStyle(gpu.mem_used_mb / gpu.mem_total_mb * 100, '#4a9eff')} />
                                </div>
                                <span style={{ color: 'var(--color-text-primary)', ...monoStyle, minWidth: 60, textAlign: 'right' as const }}>
                                  {(gpu.mem_used_mb / 1024).toFixed(1)}/{(gpu.mem_total_mb / 1024).toFixed(1)}G
                                </span>
                              </div>
                            </div>
                          )}
                          {gpu.temperature_c != null && (
                            <div style={{ ...rowStyle, paddingLeft: 8 }}>
                              <span style={{ color: 'var(--color-text-muted)' }}>Temp</span>
                              <span style={{
                                color: gpu.temperature_c > 85 ? 'var(--color-danger)' : gpu.temperature_c > 70 ? 'var(--color-warning)' : 'var(--color-text-primary)',
                                ...monoStyle,
                              }}>
                                {gpu.temperature_c}&deg;C
                              </span>
                            </div>
                          )}
                          {gpu.power_w != null && (
                            <div style={{ ...rowStyle, paddingLeft: 8 }}>
                              <span style={{ color: 'var(--color-text-muted)' }}>Power</span>
                              <span style={{ color: 'var(--color-text-primary)', ...monoStyle }}>{gpu.power_w}W</span>
                            </div>
                          )}
                        </>
                      )}
                      <div style={{ borderBottom: '1px solid var(--color-border)', marginTop: 4, marginBottom: 4 }} />
                    </>
                  );
                })()}
              </div>
            )}

            <div style={{ color: 'var(--color-text-muted)', fontSize: 9, marginBottom: 2 }}>
              INFERENCE ({stats.inference?.source || '?'})
            </div>
            {statLine('Total round-trip', stats.inference?.total)}
            {statLine('JPEG encode', stats.inference?.encode)}
            {statLine('HTTP round-trip', stats.inference?.http_roundtrip)}
            {statLine('Server inference', stats.inference?.server)}
            {statLine('Smooth mask', stats.inference?.smooth_mask)}
            {stats.inference?.network_overhead_ms != null && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, padding: '1px 0' }}>
                <span style={{ color: 'var(--color-text-secondary)' }}>Network overhead</span>
                <span style={{ color: 'var(--color-warning)', fontFamily: 'var(--font-mono)' }}>{stats.inference.network_overhead_ms}ms</span>
              </div>
            )}
            {stats.inference?.inferred_frames > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, padding: '1px 0' }}>
                <span style={{ color: 'var(--color-text-secondary)' }}>Frames</span>
                <span style={{ color: 'var(--color-text-primary)', fontFamily: 'var(--font-mono)' }}>
                  {stats.inference.inferred_frames} inferred, {stats.inference.skipped_frames} skipped
                  ({Math.round(stats.inference.skip_ratio * 100)}%)
                </span>
              </div>
            )}
            {stats.inference?.mask_stale_drops > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, padding: '1px 0' }}>
                <span style={{ color: 'var(--color-text-secondary)' }}>Stale mask drops</span>
                <span style={{ color: 'var(--color-warning)', fontFamily: 'var(--font-mono)' }}>{stats.inference.mask_stale_drops}</span>
              </div>
            )}

            <div style={{ color: 'var(--color-text-muted)', fontSize: 9, marginBottom: 2, marginTop: 6 }}>MJPEG DELIVERY</div>
            {statLine('Compose', stats.mjpeg?.compose)}
            {statLine('JPEG encode', stats.mjpeg?.encode)}
            {stats.mjpeg?.fps > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, padding: '1px 0' }}>
                <span style={{ color: 'var(--color-text-secondary)' }}>FPS</span>
                <span style={{ color: stats.mjpeg.fps >= 20 ? 'var(--color-accent)' : stats.mjpeg.fps >= 10 ? 'var(--color-warning)' : 'var(--color-danger)', fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
                  {stats.mjpeg.fps}
                </span>
              </div>
            )}

            {stats.webrtc?.fps > 0 && (
              <>
                <div style={{ color: 'var(--color-text-muted)', fontSize: 9, marginBottom: 2, marginTop: 6 }}>WEBRTC DELIVERY</div>
                {statLine('Compose', stats.webrtc?.compose)}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, padding: '1px 0' }}>
                  <span style={{ color: 'var(--color-text-secondary)' }}>FPS</span>
                  <span style={{ color: stats.webrtc.fps >= 12 ? 'var(--color-accent)' : stats.webrtc.fps >= 8 ? 'var(--color-warning)' : 'var(--color-danger)', fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
                    {stats.webrtc.fps}
                  </span>
                </div>
              </>
            )}

            {!stats.inference?.total?.avg_ms && !stats.mjpeg?.compose?.avg_ms && (
              <div style={{ color: 'var(--color-text-muted)', fontSize: 10, fontStyle: 'italic', padding: '2px 0' }}>
                No inference data yet — enable road seg with an active stream
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
